import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Coins, Plus, Trash, PencilSimple, CalendarCheck, Warning, CheckCircle,
  Receipt, Link as LinkIcon, X, FloppyDisk, ArrowSquareOut,
} from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const GOLD = "#D4A24C";
const RED = "#D90429";

const STATUTS = {
  en_cours: { label: "En cours", color: "#2563eb" },
  rapport_a_remettre: { label: "Rapport à remettre", color: RED },
  complete: { label: "Complétée", color: "#16a34a" },
  refuse: { label: "Refusée", color: "#6b7280" },
};

const EMPTY = {
  nom: "", organisme: "", projet: "", montant_total: 0, statut: "en_cours",
  date_obtention: "", contact_nom: "", contact_courriel: "", contact_tel: "",
  lien_rapport: "", affectation: "dojo", affectation_detail: "", notes: "", versements: [], echeances: [], pieces: [],
};

const AFFECT = { dojo: "Dojo", fondation: "Fondation", eleve: "Élève", athlete: "Athlète", autre: "Autre" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Ouvre un PDF protégé (Bearer) : fetch en blob puis nouvel onglet (window.open n'envoie pas le token).
async function openAuthPdf(path) {
  try {
    const resp = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error("Impossible d'ouvrir le PDF"); }
}

const daysUntil = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / 86400000);
};

export default function SubventionsModule() {
  const [subs, setSubs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/subventions");
      setSubs(data.subventions || []);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (sub) => {
    try {
      if (sub.id) await api.put(`/subventions/${sub.id}`, sub);
      else await api.post("/subventions", sub);
      toast.success("Subvention enregistrée");
      setEditing(null);
      load();
    } catch { toast.error("Erreur d'enregistrement"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette subvention ?")) return;
    try { await api.delete(`/subventions/${id}`); toast.success("Supprimée"); load(); }
    catch { toast.error("Erreur"); }
  };

  const totalActif = subs.reduce((s, x) => s + (x.montant_total || 0), 0);
  const totalRecu = subs.reduce((s, x) => s + (x.versements || []).filter(v => v.recu).reduce((a, v) => a + (v.montant || 0), 0), 0);

  return (
    <div data-testid="subventions-page" className="min-h-screen">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Suivi des subventions"
          breadcrumbs={[{ label: "Finance & administration" }, { label: "Subventions" }]}
          subtitle="Ne rate plus jamais une date limite de rapport. Tes versements, échéances et pièces justificatives en un coup d'œil."
          actions={
            <div className="flex gap-2">
              <button data-testid="btn-params" onClick={() => setParamsOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm hard-border hover:bg-secondary">⚙️ Paramètres reçus</button>
              <button data-testid="btn-batch-recus" onClick={() => setBatchOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm hard-border hover:bg-secondary">🧾 Reçus par événement</button>
              <button data-testid="btn-ai-tools" onClick={() => setAiOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm hard-border hover:bg-secondary">🤖 Outils IA</button>
              <button data-testid="btn-new-subvention" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-zinc-950 font-semibold text-sm" style={{ background: GOLD }}>
                <Plus size={16} weight="bold" /> Nouvelle subvention
              </button>
            </div>
          }
        />
        <div className="grid grid-cols-3 gap-4 mb-2 max-w-2xl">
          <HeaderStat label="Subventions actives" value={subs.length} />
          <HeaderStat label="Montant total" value={`${totalActif.toLocaleString("fr-CA")} $`} />
          <HeaderStat label="Déjà reçu" value={`${totalRecu.toLocaleString("fr-CA")} $`} accent />
        </div>
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-5">
        {subs.length === 0 && <Empty text="Aucune subvention. Clique « Nouvelle subvention » pour commencer." />}
        {subs.map((s) => <SubCard key={s.id} s={s} onEdit={() => setEditing(s)} onDelete={() => remove(s.id)} />)}
      </div>

      {editing && <EditModal sub={editing} onClose={() => setEditing(null)} onSave={save} />}
      {aiOpen && <AiToolsModal onClose={() => setAiOpen(false)} />}
      {paramsOpen && <ParamsModal onClose={() => setParamsOpen(false)} />}
      {batchOpen && <BatchReceiptsModal onClose={() => setBatchOpen(false)} />}
    </div>
  );
}

function HeaderStat({ label, value, accent }) {
  return (
    <div className="border border-zinc-800 p-3">
      <div className="ticker text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl mt-1" style={accent ? { color: GOLD } : {}}>{value}</div>
    </div>
  );
}

function SubCard({ s, onEdit, onDelete }) {
  const st = STATUTS[s.statut] || STATUTS.en_cours;
  const [sending, setSending] = useState(false);
  const openRecu = () => openAuthPdf(`/subventions/${s.id}/recu`);
  const sendRecu = async () => {
    if (!s.contact_courriel) { toast.error("Ajoute un courriel de contact d'abord."); return; }
    if (!window.confirm(`Envoyer le reçu de remerciement à ${s.contact_courriel} ?`)) return;
    setSending(true);
    try { const { data } = await api.post(`/subventions/${s.id}/envoyer-recu`); toast.success(`Reçu envoyé à ${data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    finally { setSending(false); }
  };
  const nextEch = (s.echeances || []).filter(e => e.date && !e.fait).sort((a, b) => a.date.localeCompare(b.date))[0];
  const d = nextEch ? daysUntil(nextEch.date) : null;
  const urgent = d !== null && d <= 7;
  const recu = (s.versements || []).filter(v => v.recu).reduce((a, v) => a + (v.montant || 0), 0);
  const piecesTotal = (s.pieces || []).reduce((a, p) => a + (p.montant || 0), 0);

  return (
    <div className="hard-border bg-white" data-testid={`sub-card-${s.id}`}>
      <div className="p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="ticker px-2 py-0.5 text-white" style={{ background: st.color }}>{st.label}</span>
            {s.organisme && <span className="ticker text-muted-foreground">{s.organisme}</span>}
          </div>
          <h3 className="font-display font-bold text-xl mt-2">{s.nom}</h3>
          {s.projet && <div className="text-sm text-muted-foreground">Projet : {s.projet}</div>}
          <div className="text-xs text-muted-foreground mt-1" data-testid={`affect-${s.id}`}>🎯 Affectation : <strong>{AFFECT[s.affectation] || "Dojo"}</strong>{s.affectation_detail ? ` — ${s.affectation_detail}` : ""}</div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-3xl">{(s.montant_total || 0).toLocaleString("fr-CA")} $</div>
          <div className="ticker text-muted-foreground">Reçu : {recu.toLocaleString("fr-CA")} $</div>
        </div>
      </div>

      {nextEch && (
        <div className="px-5 pb-3">
          <div className="flex items-center gap-2 px-3 py-2 hard-border text-sm font-medium" style={urgent ? { background: "#FFF1F1", borderColor: RED, color: "#991b1b" } : { background: "#FFF6E5", borderColor: GOLD, color: "#7c5e1e" }}>
            {urgent ? <Warning size={18} weight="fill" style={{ color: RED }} /> : <CalendarCheck size={18} style={{ color: GOLD }} />}
            <span><strong>{nextEch.label}</strong> — {nextEch.date} {d !== null && (d < 0 ? `(en retard de ${-d} j)` : `(dans ${d} j)`)}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
        <Section title={`Versements (${(s.versements || []).length})`}>
          {(s.versements || []).map((v, i) => (
            <Row key={i} ok={v.recu} text={`${v.label || "Versement"} — ${(v.montant || 0).toLocaleString("fr-CA")} $`} />
          ))}
          {(s.versements || []).length === 0 && <Muted />}
        </Section>
        <Section title={`Échéances (${(s.echeances || []).length})`}>
          {(s.echeances || []).map((e, i) => (
            <Row key={i} ok={e.fait} text={`${e.label || "Échéance"} — ${e.date || "?"}`} />
          ))}
          {(s.echeances || []).length === 0 && <Muted />}
        </Section>
        <Section title={`Pièces justif. — ${piecesTotal.toLocaleString("fr-CA")} $`}>
          {(s.pieces || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-0.5">
              <Receipt size={14} className="text-muted-foreground flex-shrink-0" />
              <span className="truncate flex-1">{p.description} — {(p.montant || 0).toLocaleString("fr-CA")} $</span>
              {p.lien && <a href={p.lien} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"><ArrowSquareOut size={14} /></a>}
            </div>
          ))}
          {(s.pieces || []).length === 0 && <Muted />}
        </Section>
      </div>

      <div className="p-4 flex items-center justify-between hard-border-t">
        <div className="flex items-center gap-3">
          {s.lien_rapport && <a href={s.lien_rapport} target="_blank" rel="noreferrer" className="ticker flex items-center gap-1 hover:underline" style={{ color: RED }}><LinkIcon size={14} /> Formulaire rapport</a>}
          {s.contact_courriel && <a href={`mailto:${s.contact_courriel}`} className="ticker text-muted-foreground hover:underline">{s.contact_nom || s.contact_courriel}</a>}
        </div>
        <div className="flex gap-2">
          <button data-testid={`recu-${s.id}`} onClick={openRecu} className="ticker flex items-center gap-1 px-3 py-1.5 hard-border hover:bg-secondary"><Receipt size={14} /> {s.affectation === "fondation" ? "Reçu officiel" : "Reçu"}</button>
          <button data-testid={`send-recu-${s.id}`} onClick={sendRecu} disabled={sending} className="ticker flex items-center gap-1 px-3 py-1.5 hard-border hover:bg-green-50 text-green-700 disabled:opacity-50">✉️ {sending ? "Envoi…" : "Envoyer reçu"}</button>
          <button data-testid={`edit-${s.id}`} onClick={onEdit} className="ticker flex items-center gap-1 px-3 py-1.5 hard-border hover:bg-secondary"><PencilSimple size={14} /> Modifier</button>
          <button data-testid={`delete-${s.id}`} onClick={onDelete} className="ticker flex items-center gap-1 px-3 py-1.5 hard-border hover:bg-red-50 text-red-600"><Trash size={14} /></button>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white p-4">
    <div className="ticker text-muted-foreground mb-2">{title}</div>
    <div className="space-y-0.5">{children}</div>
  </div>
);
const Row = ({ ok, text }) => (
  <div className="flex items-center gap-2 text-sm py-0.5">
    {ok ? <CheckCircle size={15} weight="fill" className="text-green-600 flex-shrink-0" /> : <div className="w-[15px] h-[15px] border border-muted-foreground rounded-full flex-shrink-0" />}
    <span className={ok ? "line-through text-muted-foreground" : ""}>{text}</span>
  </div>
);
const Muted = () => <div className="text-sm text-muted-foreground">—</div>;
const Empty = ({ text }) => <div className="hard-border p-8 text-center text-muted-foreground bg-secondary/20">{text}</div>;

// ─── Modale d'édition ───────────────────────────────────────────────────────
function EditModal({ sub, onClose, onSave }) {
  const [f, setF] = useState({ ...EMPTY, ...sub });
  const up = (k, v) => setF({ ...f, [k]: v });
  const upList = (key, i, k, v) => { const l = [...f[key]]; l[i] = { ...l[i], [k]: v }; up(key, l); };
  const addItem = (key, item) => up(key, [...(f[key] || []), item]);
  const delItem = (key, i) => up(key, f[key].filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-background hard-border max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="sub-edit-modal">
        <div className="hard-border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-background z-10">
          <div className="font-display font-bold text-lg">{sub.id ? "Modifier la subvention" : "Nouvelle subvention"}</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom de la subvention *" v={f.nom} on={(v) => up("nom", v)} testid="f-nom" />
            <Field label="Organisme" v={f.organisme} on={(v) => up("organisme", v)} testid="f-organisme" />
            <Field label="Projet" v={f.projet} on={(v) => up("projet", v)} />
            <Field label="Montant total ($)" type="number" v={f.montant_total} on={(v) => up("montant_total", parseFloat(v || 0))} testid="f-montant" />
            <div>
              <label className="ticker text-muted-foreground block mb-2">Statut</label>
              <select value={f.statut} onChange={(e) => up("statut", e.target.value)} className="w-full hard-border px-3 py-2.5" data-testid="f-statut">
                {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <Field label="Date d'obtention" type="date" v={f.date_obtention} on={(v) => up("date_obtention", v)} />
            <Field label="Contact (nom)" v={f.contact_nom} on={(v) => up("contact_nom", v)} />
            <Field label="Contact (courriel)" v={f.contact_courriel} on={(v) => up("contact_courriel", v)} />
            <Field label="Contact (tél.)" v={f.contact_tel} on={(v) => up("contact_tel", v)} />
            <Field label="Lien du formulaire de rapport" v={f.lien_rapport} on={(v) => up("lien_rapport", v)} />
            <div>
              <label className="ticker text-muted-foreground block mb-2">Affectation (pour qui / quoi)</label>
              <select value={f.affectation} onChange={(e) => up("affectation", e.target.value)} className="w-full hard-border px-3 py-2.5" data-testid="f-affectation">
                {Object.entries(AFFECT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <Field label="Raison / événement précis" v={f.affectation_detail} on={(v) => up("affectation_detail", v)} testid="f-affectation-detail" />
          </div>

          <ListEditor title="Versements" items={f.versements} onAdd={() => addItem("versements", { label: "", montant: 0, recu: false })} onDel={(i) => delItem("versements", i)}
            render={(v, i) => (
              <div className="grid grid-cols-12 gap-2 items-center">
                <input className="col-span-5 hard-border px-2 py-1.5 text-sm" placeholder="Label" value={v.label} onChange={(e) => upList("versements", i, "label", e.target.value)} />
                <input className="col-span-4 hard-border px-2 py-1.5 text-sm" type="number" placeholder="Montant" value={v.montant} onChange={(e) => upList("versements", i, "montant", parseFloat(e.target.value || 0))} />
                <label className="col-span-3 ticker flex items-center gap-1"><input type="checkbox" checked={v.recu} onChange={(e) => upList("versements", i, "recu", e.target.checked)} /> Reçu</label>
              </div>
            )} />

          <ListEditor title="Échéances" items={f.echeances} onAdd={() => addItem("echeances", { label: "", date: "", fait: false })} onDel={(i) => delItem("echeances", i)}
            render={(e, i) => (
              <div className="grid grid-cols-12 gap-2 items-center">
                <input className="col-span-6 hard-border px-2 py-1.5 text-sm" placeholder="Label (ex. Rapport final)" value={e.label} onChange={(ev) => upList("echeances", i, "label", ev.target.value)} />
                <input className="col-span-4 hard-border px-2 py-1.5 text-sm" type="date" value={e.date || ""} onChange={(ev) => upList("echeances", i, "date", ev.target.value)} />
                <label className="col-span-2 ticker flex items-center gap-1"><input type="checkbox" checked={e.fait} onChange={(ev) => upList("echeances", i, "fait", ev.target.checked)} /> Fait</label>
              </div>
            )} />

          <ListEditor title="Pièces justificatives" items={f.pieces} onAdd={() => addItem("pieces", { description: "", montant: 0, lien: "" })} onDel={(i) => delItem("pieces", i)}
            render={(p, i) => (
              <div className="grid grid-cols-12 gap-2 items-center">
                <input className="col-span-4 hard-border px-2 py-1.5 text-sm" placeholder="Description" value={p.description} onChange={(e) => upList("pieces", i, "description", e.target.value)} />
                <input className="col-span-3 hard-border px-2 py-1.5 text-sm" type="number" placeholder="Montant" value={p.montant} onChange={(e) => upList("pieces", i, "montant", parseFloat(e.target.value || 0))} />
                <input className="col-span-5 hard-border px-2 py-1.5 text-sm" placeholder="Lien (WellyBox…)" value={p.lien || ""} onChange={(e) => upList("pieces", i, "lien", e.target.value)} />
              </div>
            )} />

          <div>
            <label className="ticker text-muted-foreground block mb-2">Notes</label>
            <textarea value={f.notes || ""} onChange={(e) => up("notes", e.target.value)} className="w-full hard-border px-3 py-2 text-sm" rows={2} />
          </div>
        </div>
        <div className="hard-border-t p-5 flex justify-end sticky bottom-0 bg-background">
          <button data-testid="save-subvention" onClick={() => f.nom ? onSave(f) : toast.error("Le nom est requis")} className="flex items-center gap-2 px-6 py-2.5 ticker text-zinc-950" style={{ background: GOLD }}>
            <FloppyDisk size={16} /> Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="ticker text-muted-foreground block mb-2">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full hard-border px-3 py-2.5" data-testid={testid} />
    </div>
  );
}

function ListEditor({ title, items, render, onAdd, onDel }) {
  return (
    <div className="hard-border p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="ticker font-semibold">{title}</div>
        <button onClick={onAdd} className="ticker flex items-center gap-1 px-2 py-1 hover:text-[#D90429]"><Plus size={13} /> Ajouter</button>
      </div>
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex-1">{render(it, i)}</div>
            <button onClick={() => onDel(i)} className="text-red-500 hover:text-red-700"><Trash size={15} /></button>
          </div>
        ))}
        {(items || []).length === 0 && <div className="text-sm text-muted-foreground">Aucun élément.</div>}
      </div>
    </div>
  );
}

// ─── Outils IA : reco de montant de commandite + lettre de demande ──────────
function AiToolsModal({ onClose }) {
  const [tab, setTab] = useState("montant");
  const [evenement, setEvenement] = useState("");
  const [organisme, setOrganisme] = useState("");
  const [montant, setMontant] = useState("");
  const [affectation, setAffectation] = useState("dojo");
  const [typeEvenement, setTypeEvenement] = useState("gala");
  const [contexte, setContexte] = useState("");
  const [loading, setLoading] = useState(false);
  const [reco, setReco] = useState(null);
  const [lettre, setLettre] = useState("");
  const [library, setLibrary] = useState([]);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);

  const loadLib = async () => { try { const { data } = await api.get("/event-letters"); setLibrary(data.letters || []); } catch {} };
  useEffect(() => { loadLib(); }, []);

  const runMontant = async () => {
    if (!evenement.trim()) { toast.error("Décris l'événement/projet."); return; }
    setLoading(true); setReco(null);
    try { const { data } = await api.post("/subventions/ia/montant", { evenement, contexte }); setReco(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
    finally { setLoading(false); }
  };
  const runLettre = async () => {
    if (!evenement.trim()) { toast.error("Décris l'événement/projet."); return; }
    setLoading(true); setLettre("");
    try { const { data } = await api.post("/subventions/ia/lettre", { evenement, type_evenement: typeEvenement, organisme, montant: montant ? parseFloat(montant) : null, affectation, contexte }); setLettre(data.lettre); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
    finally { setLoading(false); }
  };
  const saveLettre = async () => {
    if (!lettre.trim()) return;
    try {
      await api.post("/event-letters", { titre: evenement || "Lettre", type_evenement: typeEvenement, organisme, annee: new Date().getFullYear(), contenu: lettre });
      toast.success("Lettre enregistrée dans la bibliothèque");
      loadLib();
    } catch { toast.error("Erreur d'enregistrement"); }
  };
  const delLettre = async (id) => { try { await api.delete(`/event-letters/${id}`); loadLib(); } catch {} };
  const sendLettre = async () => {
    const to = sendTo.trim();
    if (!to || !to.includes("@")) { toast.error("Entre l'adresse courriel du commanditaire."); return; }
    if (!lettre.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/subventions/ia/lettre/envoyer", { to, lettre, organisme, objet: `Demande de commandite${evenement ? " — " + evenement : ""} · Institut Impact Élite Martial` });
      toast.success(`Lettre envoyée à ${data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi échoué"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-background hard-border max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="ai-tools-modal">
        <div className="hard-border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-background z-10">
          <div className="font-display font-bold text-lg">🤖 Outils IA — Commandites & subventions</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="px-6 pt-4 flex gap-2">
          <button onClick={() => setTab("montant")} data-testid="ai-tab-montant" className={`ticker px-3 py-2 hard-border ${tab === "montant" ? "bg-secondary font-bold" : ""}`}>💵 Montant suggéré</button>
          <button onClick={() => setTab("lettre")} data-testid="ai-tab-lettre" className={`ticker px-3 py-2 hard-border ${tab === "lettre" ? "bg-secondary font-bold" : ""}`}>✍️ Lettre de demande</button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Événement / projet *" v={evenement} on={setEvenement} testid="ai-evenement" />
          <Field label="Contexte (région, situation…)" v={contexte} on={setContexte} />
          {tab === "lettre" && <>
            <Field label="Destinataire (organisme)" v={organisme} on={setOrganisme} />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="ticker text-muted-foreground block mb-2">Type d'événement</label>
                <select value={typeEvenement} onChange={(e) => setTypeEvenement(e.target.value)} className="w-full hard-border px-3 py-2.5" data-testid="ai-type-evenement">
                  {[["gala","Gala"],["tournoi","Tournoi"],["camp","Camp"],["programme_jeunesse","Programme jeunesse"],["levee_de_fonds","Levée de fonds"],["spectacle","Spectacle"],["autre","Autre"]].map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <Field label="Montant demandé ($)" type="number" v={montant} on={setMontant} />
              <div>
                <label className="ticker text-muted-foreground block mb-2">Affectation</label>
                <select value={affectation} onChange={(e) => setAffectation(e.target.value)} className="w-full hard-border px-3 py-2.5">
                  {Object.entries(AFFECT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
          </>}

          {tab === "montant" ? (
            <button onClick={runMontant} disabled={loading} data-testid="ai-run-montant" className="px-5 py-2.5 rounded-lg text-zinc-950 font-semibold text-sm disabled:opacity-50" style={{ background: GOLD }}>{loading ? "Analyse…" : "💵 Recommander un montant"}</button>
          ) : (
            <button onClick={runLettre} disabled={loading} data-testid="ai-run-lettre" className="px-5 py-2.5 rounded-lg text-zinc-950 font-semibold text-sm disabled:opacity-50" style={{ background: GOLD }}>{loading ? "Rédaction…" : "✍️ Générer la lettre"}</button>
          )}

          {reco && tab === "montant" && (
            <div className="hard-border p-4 space-y-3" data-testid="ai-reco-result">
              <div className="grid grid-cols-3 gap-2">
                {(reco.paliers || []).map((p, i) => (
                  <div key={i} className="hard-border p-3 text-center">
                    <div className="ticker text-muted-foreground">{p.nom}</div>
                    <div className="font-display font-black text-2xl">{p.montant} $</div>
                    <div className="text-xs text-muted-foreground mt-1">{p.avantages}</div>
                  </div>
                ))}
              </div>
              <div className="text-sm"><strong>Recommandé : {reco.recommande} $</strong> — {reco.justification}</div>
              {reco.projection_avenir && <div className="text-xs text-muted-foreground">📈 {reco.projection_avenir}</div>}
            </div>
          )}
          {lettre && tab === "lettre" && (
            <div className="space-y-2" data-testid="ai-lettre-result">
              <textarea readOnly value={lettre} className="w-full hard-border p-3 text-sm h-64 font-mono" />
              <div className="flex gap-2">
                <button onClick={async () => { try { await navigator.clipboard.writeText(lettre); toast.success("Lettre copiée"); } catch {} }} className="ticker px-3 py-1.5 hard-border hover:bg-secondary">📋 Copier</button>
                <button onClick={saveLettre} data-testid="save-lettre-btn" className="ticker px-3 py-1.5 hard-border hover:bg-secondary text-green-700">💾 Enregistrer dans la bibliothèque</button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center pt-2 border-t mt-2">
                <input value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="Courriel du commanditaire" data-testid="letter-send-to" className="flex-1 hard-border px-3 py-2 text-sm" />
                <button onClick={sendLettre} disabled={sending} data-testid="send-letter-btn" className="ticker px-4 py-2 hard-border hover:bg-green-50 text-green-700 disabled:opacity-50 whitespace-nowrap">✉️ {sending ? "Envoi…" : "Envoyer au commanditaire"}</button>
              </div>
            </div>
          )}
          {tab === "lettre" && library.length > 0 && (
            <div className="mt-4 border-t pt-3 space-y-2" data-testid="letter-library">
              <div className="ticker text-muted-foreground">📚 Bibliothèque de lettres ({library.length})</div>
              {library.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-2 text-sm hard-border p-2" data-testid={`lib-letter-${l.id}`}>
                  <div className="min-w-0"><strong className="truncate">{l.titre}</strong> <span className="text-muted-foreground text-xs">· {l.type_evenement} · {l.annee}</span></div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => { setLettre(l.contenu); setTab("lettre"); }} className="text-xs px-2 py-1 hard-border hover:bg-secondary">Charger</button>
                    <button onClick={() => delLettre(l.id)} className="text-xs px-2 py-1 hard-border hover:bg-red-50 text-red-600">✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Paramètres reçus : n° de bienfaisance de la Fondation ──────────────────
function ParamsModal({ onClose }) {
  const [charity, setCharity] = useState("");
  const [fond, setFond] = useState("");
  useEffect(() => { (async () => { try { const { data } = await api.get("/org/parametres"); setCharity(data.charity_number || ""); setFond(data.fondation_nom || ""); } catch {} })(); }, []);
  const save = async () => {
    try { await api.put("/org/parametres", { charity_number: charity, fondation_nom: fond }); toast.success("Paramètres enregistrés"); onClose(); }
    catch { toast.error("Erreur d'enregistrement"); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-background hard-border max-w-md w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="params-modal">
        <div className="hard-border-b px-6 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-lg">⚙️ Paramètres des reçus</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">Ces informations apparaissent automatiquement sur les <strong>reçus officiels de don</strong> (affectation « Fondation »).</p>
          <Field label="Nom officiel de la Fondation" v={fond} on={setFond} testid="param-fond" />
          <Field label="N° d'enregistrement d'organisme de bienfaisance (ARC)" v={charity} on={setCharity} testid="param-charity" />
          <button onClick={save} data-testid="param-save" className="px-5 py-2.5 rounded-lg text-zinc-950 font-semibold text-sm" style={{ background: GOLD }}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}


// ─── Reçus en lot : tous les donateurs d'un même événement ──────────────────
function BatchReceiptsModal({ onClose }) {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/subventions/evenements"); setEvents(data.evenements || []); }
    catch { toast.error("Erreur de chargement des événements"); }
  })(); }, []);

  const cur = events.find((e) => e.evenement === selected);
  const [donors, setDonors] = useState([]);
  const [loadingDonors, setLoadingDonors] = useState(false);

  useEffect(() => {
    if (!selected) { setDonors([]); return; }
    (async () => {
      setLoadingDonors(true);
      try { const { data } = await api.get(`/subventions/evenement/donateurs?evenement=${encodeURIComponent(selected)}`); setDonors(data.donors || []); }
      catch { setDonors([]); }
      finally { setLoadingDonors(false); }
    })();
  }, [selected]);
  const sendAll = async () => {
    if (!selected) { toast.error("Choisis un événement."); return; }
    if (!window.confirm(`Envoyer le reçu de remerciement à tous les donateurs de « ${selected} » ?`)) return;
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/subventions/evenement/envoyer-recus", { evenement: selected });
      setResult(data);
      toast.success(`${data.sent} reçu(s) envoyé(s)${data.skipped ? ` · ${data.skipped} sans courriel` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi échoué"); }
    finally { setSending(false); }
  };
  const openPdf = () => {
    if (!selected) { toast.error("Choisis un événement."); return; }
    openAuthPdf(`/subventions/evenement/recus-pdf?evenement=${encodeURIComponent(selected)}`);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-background hard-border max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="batch-recus-modal">
        <div className="hard-border-b px-6 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-lg">🧾 Reçus en lot — par événement</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">Génère et envoie en un clic le reçu de remerciement à <strong>tous les donateurs d'un même événement</strong> (regroupés par « Raison / événement »).</p>
          <div>
            <label className="ticker text-muted-foreground block mb-2">Événement</label>
            <select value={selected} onChange={(e) => { setSelected(e.target.value); setResult(null); }} className="w-full hard-border px-3 py-2.5" data-testid="batch-event-select">
              <option value="">— Choisir un événement —</option>
              {events.map((e) => <option key={e.evenement} value={e.evenement}>{e.evenement} ({e.donors} donateur(s) · {e.with_email} avec courriel)</option>)}
            </select>
          </div>
          {events.length === 0 && <div className="text-sm text-muted-foreground">Aucun événement trouvé. Ajoute une « Raison / événement » sur tes subventions/dons.</div>}
          {cur && (
            <div className="hard-border p-3 text-sm" data-testid="batch-event-summary">
              <div>{cur.donors} donateur(s) · <strong>{cur.with_email}</strong> avec courriel · Total : {(cur.total || 0).toLocaleString("fr-CA")} $</div>
            </div>
          )}
          {selected && (
            <div className="hard-border max-h-56 overflow-y-auto" data-testid="batch-donor-list">
              <div className="ticker text-muted-foreground px-3 py-2 border-b bg-secondary/40 sticky top-0">Donateurs qui recevront un reçu</div>
              {loadingDonors && <div className="px-3 py-3 text-sm text-muted-foreground">Chargement…</div>}
              {!loadingDonors && donors.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Aucun donateur.</div>}
              {donors.map((d, i) => (
                <div key={i} className="px-3 py-2 flex items-center justify-between text-sm border-b last:border-b-0" data-testid={`donor-row-${i}`}>
                  <div>
                    <div className="font-medium">{d.nom}</div>
                    <div className={`text-xs ${d.a_courriel ? "text-muted-foreground" : "text-amber-600"}`}>{d.a_courriel ? d.courriel : "⚠️ Aucun courriel — sera ignoré"}</div>
                  </div>
                  <div className="text-right whitespace-nowrap">{(d.montant || 0).toLocaleString("fr-CA")} $ {d.a_courriel ? <span className="text-green-600">✉️</span> : ""}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button onClick={sendAll} disabled={sending || !selected} data-testid="batch-send-btn" className="ticker px-4 py-2 hard-border hover:bg-green-50 text-green-700 disabled:opacity-50">✉️ {sending ? "Envoi…" : "Envoyer tous les reçus"}</button>
            <button onClick={openPdf} disabled={!selected} data-testid="batch-pdf-btn" className="ticker px-4 py-2 hard-border hover:bg-secondary disabled:opacity-50">📄 PDF combiné (impression)</button>
          </div>
          {result && (
            <div className="hard-border p-3 text-sm space-y-1" data-testid="batch-result">
              <div className="text-green-700">✅ {result.sent} envoyé(s)</div>
              {result.skipped > 0 && <div className="text-amber-600">⚠️ {result.skipped} sans courriel : {(result.skipped_names || []).join(", ")}</div>}
              {result.errors > 0 && <div className="text-red-600">❌ {result.errors} en erreur : {(result.errors_to || []).join(", ")}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
